import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Header, Button, Message, Icon } from 'semantic-ui-react';
import { LoadingWrapper, ReactTable, SafeButton, useMediaQuery } from '../common';
import { useAuth } from '../auth';
import { getFormChecklists, deleteFormChecklist } from './formChecklistsService';
import { formatDateForDisplay } from '../../utils/formatDate';
import './FormChecklist.css';

export const FormChecklistList = () => {
    const navigate = useNavigate();
    const { userIsIccSupportTeam, isReadOnly } = useAuth();
    const isMobile = useMediaQuery('(max-width: 767px)');

    const [checklists, setChecklists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedRows, setSelectedRows] = useState({});

    const fetchChecklists = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getFormChecklists();
            setChecklists(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchChecklists();
    }, []);

    const deleteSelectedChecklists = async () => {
        const selectedIds = Object.entries(selectedRows)
            .filter(([, checked]) => checked)
            .map(([id]) => id);
        const errored = [];
        for (const id of selectedIds) {
            try {
                await deleteFormChecklist(id);
            } catch {
                const match = checklists.find(c => String(c.id) === id);
                errored.push(match?.name ?? id);
            }
        }
        if (errored.length > 0) {
            setError(`Failed to delete: ${errored.join(', ')}`);
        }
        setSelectedRows({});
        await fetchChecklists();
    };

    const handleSelectionChange = useCallback((rowMap) => {
        const idMap = {};
        Object.entries(rowMap).forEach(([idx, checked]) => {
            if (checklists[idx]) idMap[checklists[idx].id] = checked;
        });
        setSelectedRows(idMap);
    }, [checklists]);

    const handleDelete = async (id, name) => {
        if (!window.confirm(`Delete checklist "${name}"?`)) return;
        try {
            await deleteFormChecklist(id);
            await fetchChecklists();
        } catch (err) {
            setError(err.message);
        }
    };

    const hasSelection = Object.values(selectedRows).some(Boolean);
    const canBulkSelect = userIsIccSupportTeam && !isReadOnly && !isMobile;

    const columns = [
        {
            Header: 'Name',
            accessor: 'name',
            minWidth: 160,
            Cell: ({ row, value }) => (
                <Link to={`/documents/form-checklists/${row.original.id}`} onClick={e => e.stopPropagation()}>
                    {value}
                </Link>
            ),
        },
        { Header: 'Activity', accessor: 'activity', minWidth: 120 },
        { Header: 'Condition', accessor: 'condition', minWidth: 120 },
        { Header: 'Standard', accessor: 'standard', minWidth: 100 },
        {
            Header: 'Created',
            accessor: 'creationTime',
            minWidth: 110,
            Cell: ({ value }) => (value ? formatDateForDisplay(new Date(value)) : '—'),
        },
        {
            Header: 'Actions',
            id: 'actions',
            minWidth: 100,
            Cell: ({ row }) => (
                <div onClick={e => e.stopPropagation()} className="fc-list-actions">
                    <Button
                        size="tiny"
                        icon
                        onClick={() => navigate(`/documents/form-checklists/${row.original.id}`)}
                        title="View"
                    >
                        <Icon name="eye" />
                    </Button>
                    {userIsIccSupportTeam && !isReadOnly && (
                        <>
                            <Button
                                size="tiny"
                                icon
                                onClick={() => navigate(`/documents/form-checklists/${row.original.id}/edit`)}
                                title="Edit"
                            >
                                <Icon name="edit" />
                            </Button>
                            <Button
                                size="tiny"
                                icon
                                color="red"
                                onClick={() => handleDelete(row.original.id, row.original.name)}
                                title="Delete"
                            >
                                <Icon name="trash" />
                            </Button>
                        </>
                    )}
                </div>
            ),
        },
    ];

    return (
        <div className="fc-list-container">
            <Header as="h2">Forms and Checklists</Header>

            {error && <Message negative content={error} />}

            <LoadingWrapper loading={loading}>
                {checklists.length === 0 && !loading ? (
                    <Message info content="No checklists found." />
                ) : (
                    <div className="fc-checklist-table-wrapper">
                        <ReactTable
                            data={checklists}
                            columns={columns}
                            mobileHiddenColumns={['condition', 'standard', 'creationTime']}
                            className="fc-checklist-table react-table-mobile-compact"
                            editable={canBulkSelect}
                            onSelectionChange={handleSelectionChange}
                            truncateCells
                            onRowClick={row => navigate(`/documents/form-checklists/${row.original.id}`)}
                        />
                    </div>
                )}
            </LoadingWrapper>
            {userIsIccSupportTeam && !isReadOnly && (
                <div style={{ paddingBottom: '1rem', paddingTop: '1rem' }}>
                    <Button primary onClick={() => navigate('/documents/form-checklists/new')}>
                        <Icon name="plus" />
                        Add Checklist
                    </Button>
                    {canBulkSelect && (
                        <SafeButton
                            style={{ backgroundColor: 'var(--delete)', color: 'white', marginLeft: '0.5rem' }}
                            submitText="Delete selected"
                            disabled={!hasSelection}
                            submitAction={deleteSelectedChecklists}
                        />
                    )}
                </div>
            )}
        </div>
    );
};
